import type {
  CreateProvider,
  DraftProvider,
  ModelListResult,
  ProviderCheckResult,
  ProviderView,
  UpdateProvider,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

export function useProviders() {
  const { tenant } = useSession();
  return useQuery({
    queryKey: ['providers', tenant?.id],
    queryFn: () => api<ProviderView[]>('GET', '/ai/providers'),
    enabled: tenant?.kind === 'platform',
  });
}

/** Mutations refresh the list: changing the default or saving one provider can change others too. */
function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['providers'] });
}

export function useCreateProvider() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (body: CreateProvider) => api<ProviderView>('POST', '/ai/providers', body),
    onSuccess: refresh,
  });
}

export function useUpdateProvider() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateProvider & { id: string }) =>
      api<ProviderView>('PATCH', `/ai/providers/${id}`, body),
    onSuccess: refresh,
  });
}

export function useDeleteProvider() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ id, revision }: { id: string; revision: number }) =>
      api<{ deleted: true }>('DELETE', `/ai/providers/${id}`, { revision }),
    onSuccess: refresh,
  });
}

export function useCheckProvider() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ result: ProviderCheckResult; provider: ProviderView }>('POST', `/ai/providers/${id}/check`),
    onSettled: refresh,
  });
}

export const checkDraft = (draft: DraftProvider) =>
  api<ProviderCheckResult>('POST', '/ai/providers/check', draft);

export const listModels = (draft: DraftProvider) =>
  api<ModelListResult>('POST', '/ai/providers/models', draft);
